import { useMemo } from 'react';
import { resolveLayout } from '../autoGrid';
import { canvasHeightPx, computeGridGeometry, widgetRectPx } from '../gridGeometry';
import { useContainerWidth } from '../useContainerWidth';
import { WidgetLayout } from '../types';
import WidgetCard from '../WidgetCard/WidgetCard';
import { resolveWidgetStepTint } from '../WidgetCard/tints';
import { MobileWidget, WebWidget } from '../Widgets';
import type { WidgetDataState } from '../Widgets/widgetData';
import classes from './WidgetLayoutViewer.module.css';
import { Widget } from '../Widgets/types';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';


type Props = {
  layout: WidgetLayout;
  widgetDataMap: Record<string, WidgetDataState>;
  scale?: number; // 1 == 100 %
  /**
   * Does the report sit on a PAGE, or in the page? (2026-08-17)
   *
   * - `'paper'` (default) — a white sheet with a padded frame around it. Right for
   *   **Reports**: a report is a document, the sheet is what makes it feel like one,
   *   and it is the thing that will eventually be printed.
   * - `'plain'` — no sheet, no frame; the cards sit directly on whatever is behind
   *   them. Right for the **Dashboard**, where the row is one band among bespoke
   *   sections that have no sheet of their own. A white rectangle around only the
   *   widget-engine band made it read as an embedded document rather than part of
   *   the page. (It used to be one band among THREE — `SelectedProductsPublications`,
   *   `KeyIndicators` and `AlertsSection`. TV1-2343 pulled the latter two INTO the
   *   report as `count` / `alert` widgets and deleted them, so the surrounding
   *   sections are now just the toolbar and the publications list. The reasoning is
   *   unchanged: the page still has no sheet, so neither may this.)
   *
   * A prop rather than a CSS override at the call site because the CARDS have to
   * know too: an untinted card is transparent and inherits whatever is under it, so
   * on `plain` it needs its own surface or it would disappear into the page.
   */
  surface?: 'paper' | 'plain';
};

const WidgetLayoutViewer = ({ layout, widgetDataMap, scale = 1, surface = 'paper' }: Props ) => {
  const { t } = useTranslation( 'widgets' );
  // TV1-2291 (S1/S2) — one definition of the pixel math, shared with the editor,
  // over the width the viewer is actually GIVEN rather than a hard-coded 827px.
  const [ containerRef, containerWidth ] = useContainerWidth<HTMLDivElement>();
  const geometry = useMemo(
    () => computeGridGeometry( layout, { scale, containerWidthPx: containerWidth }),
    [ layout, scale, containerWidth ]
  );
  const { device } = geometry;

  // TV1-1931 — resolve positionless widgets into the auto-grid before rendering.
  // The viewer is READ-ONLY: this never writes back. Without it, `w.y! * rowPx`
  // on a positionless widget silently yields `top: NaN`.
  //
  // TV1-2291 (§1) — and because it is read-only, it needs only ONE resolution:
  // the clamped, DISPLAY one. The editor has to keep the authored resolution
  // alongside it (SDD K3 — mutating the clamped layout would persist a phone's
  // widths over the desktop arrangement); a reader never writes, so passing the
  // geometry here is unconditionally safe and is the whole responsive behaviour.
  //
  // `scale` is passed separately on purpose: `geometry.colPx` carries the zoom,
  // and the legibility minimum must be computed against the 1x column width, so
  // `resolveLayout( layout, geometry )` does not compile (`GridGeometry` has no
  // `scale`). Zoom magnifies an unchanged layout; it must never reflow it.
  const resolvedWidgets = useMemo(
    () => resolveLayout( layout, { colPx: geometry.colPx, scale, gapPx: geometry.gapPx }).widgets,
    [ layout, geometry.colPx, geometry.gapPx, scale ]
  );

  /**
   * ZERO slack rows (2026-08-21).
   *
   * Slack exists so the EDITOR has somewhere to drag a widget to; a reader has
   * nowhere to drag anything, so every slack row it renders is dead space. The
   * viewer used to ask for 1, which put a full `ROW_HEIGHT_PX` (40px × scale) of
   * empty canvas under the last widget — invisible on a report sheet, but on the
   * Dashboard it lands between the KPI row and the alert cards and reads as a
   * mysteriously large gap that no margin in either component accounts for. That
   * is exactly how it was reported.
   *
   * `canvasHeightPx( …, 0 )` is still tall enough to contain every card: each
   * card's box is `y * rowPx + height * rowPx`, so `bottomRow * rowPx` bounds
   * them exactly.
   */
  const canvasHeight = useMemo(() => {
    if ( !resolvedWidgets.length ) return '100vh';
    return canvasHeightPx( resolvedWidgets, geometry, 0 );
  }, [ resolvedWidgets, geometry ]);

  const renderWidgetForDevice = ( device: string, widget: Widget ) => {
    const deviceComponents: Record<string, ( widget: Widget ) => JSX.Element> = {
      web: ( w ) => (
        <WebWidget
          widget={w}
          data={widgetDataMap[w.definition.id]}
          type={w.definition.type}
          widgetComponentProps={w.definition.widgetComponentProps}
        />
      ),
      mobile: ( w ) => <MobileWidget
          widget={w}
          data={widgetDataMap[w.definition.id]}
          type={w.definition.type}
          widgetComponentProps={w.definition.widgetComponentProps}
        />
    };
  
    const renderComponent = deviceComponents[device];
    return renderComponent ? renderComponent( widget ) : <>{t( 'unsupported_device','unsupported device' )}</>;
  };
  
  return (
    <div
      className={`${ classes.canvasWrapper } ${ surface === 'plain' ? classes.plain : '' }`}
      data-surface={surface}
      ref={containerRef}
    >
    <div
      className={classes.canvas}
      style={{
        width: geometry.canvasWidthPx,
        height: canvasHeight
      }}
    >
      {/* TV1-2291 (t11) — the card surface is SHARED with the editor
          (`WidgetCard`), because a tint has to render for readers too. Note the
          absent `chrome` prop: that slot is how the editor injects its action
          bar, and a reader gets no edit affordance because there is nothing to
          fill it with here — not because a `readOnly` flag was remembered. */}
      {resolvedWidgets.map(( w ) => (
        <WidgetCard
          key={w.definition.id}
          widget={w}
          // A colour STEP paints the whole card, so it has to be resolved where the
          // widget AND its fetched value are both in scope — which is here, not in
          // the card. See `resolveWidgetStepTint`.
          stepTint={resolveWidgetStepTint( w, widgetDataMap[w.definition.id])}
          testId={componentsTestIds.widgetView( w.definition.id )}
          className={classes.widget}
          style={widgetRectPx( w, geometry )}
        >
          {renderWidgetForDevice( device, w )}
        </WidgetCard>
      ))}
    </div>
    </div>
  );
};

export default WidgetLayoutViewer;